import React, { useEffect, useState } from 'react';
import { Form, useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import lodash from 'lodash';
import setLocalValue from '../utils/setLocalValue';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';
import { logEvent, signInUser } from '../utils/analytics';
import { RESET_PASSWORD_MODAL_ID } from '../modals/ResetPasswordModal';
import { IN_DEVELOPMENT } from '../constants';
import EVENTS from '../constants/events';
import redcatApiFetch from '../api';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import { syncMember } from '../thunks/syncMember';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { useTranslation } from 'react-i18next';
import StandardButton from '../components/StandardButton';
import { REGISTER_MODAL_ID } from '../modals/RegisterModal';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import FormTextField from '../components/FormTextField';
import { OrderingSelectors } from 'polygon-ordering';
import i18next from 'i18next';
import OAuthlogin from './OAuthLogin';
import ScreenLink from './ScreenLink';
import FormCheckBox from './FormCheckBox';
import { removeSavedUsername, setSavedUsername } from '../slices/savedUsername';

export const SIGN_IN_MODAL_ID = 'SIGN_IN_MODAL_ID';
const { getGuestOrderingAvailable, getMember } = OrderingSelectors;

const login = (
  { email, password, reCaptchaToken, mfaToken, mfaCode }: LoginParams,
  successCallback: (result: any) => void,
  finallyCallback: () => void,
  mfaCallback: (token: string) => void,
) => {
  let skipFinally = false;

  redcatApiFetch({
    path: '/api/v1/login',
    method: 'POST',
    body: {
      username: email,
      psw: password,
      'g-recaptcha-response': reCaptchaToken,
      tfa_pin: mfaCode,
      tfa_token: mfaToken,
      auth_type: 'M',
      save_session: '1',
      next: '/',
    },
  })
    .then((result: any) => {
      skipFinally = true;
      logEvent(EVENTS.SIGN_IN);
      successCallback(result);
      signInUser({
        id: result.data.ID,
        givenName: result.data.GivenName,
        surname: result.data.Surname,
        email: result.data.Email,
        phoneNumber: result.data.Mobile ?? result.data.Phone,
        state: result.data.State,
        city: result.data.Suburb,
        zipCode: result.data.Postcode,
        dateOfBirth: result.data.DateOfBirth,
      });
    })
    .catch((e: any) => {
      const data = lodash.get(e, 'details.json', {});
      if (data.error_code === 3) {
        enqueueSuccessSnackbar(i18next.t(`errorCode.${data.error_code}`));
        mfaCallback(data.additional_info);
      } else {
        logEvent(EVENTS.SIGN_IN_FAILED);
        enqueueErrorSnackbar(e);
      }
    })
    .then(() => {
      if (!skipFinally) {
        finallyCallback();
      }
    });
};

const SignInForm: React.FC<{ onSuccess?: () => void }> = ({ onSuccess }) => {
  const [reCaptchaToken, setReCaptchaToken] = useState<null | string>(null);
  const [mfaToken, setMfaToken] = useState<null | string>(null);
  const savedUsername = useAppSelector(state => state.savedUsername);
  const { t } = useTranslation();
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const enableReCaptcha = useAppSelector(state => state.ordering.config.enableReCaptcha);
  const reCaptchaSiteKey = useAppSelector(state => state.config.reCaptchaSiteKey);
  const member = useAppSelector(getMember);

  useEffect(() => {
    if (member) {
      dispatch(setCurrentModal(null));
    }
  }, [member]);

  const dispatch = useAppDispatch();
  const guestOrderingEnabled = useAppSelector(getGuestOrderingAvailable);

  const schemas: SchemaType = {
    email: Yup.string().email(t('form.emailValidation')).required(t('form.passwordTooShort')),
    password: Yup.string().required(t('form.required')).min(2, t('form.passwordTooShort')),
  };

  if (mfaToken) {
    schemas.mfaCode = Yup.string().required(t('form.required')).min(4, t('form.passwordTooShort'));
  }

  const validationSchema = Yup.object().shape(schemas);

  const onSubmit = (
    {
      email,
      password,
      mfaCode,
      rememberMe,
    }: { email: string; password: string; mfaCode: string; rememberMe: boolean },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    const params: LoginParams = {
      email,
      password,
      mfaToken,
      mfaCode,
    };

    if (enableReCaptcha && reCaptchaToken) {
      params.reCaptchaToken = reCaptchaToken;
    }

    login(
      params,
      result => {
        dispatch(setCurrentModal(null));
        if (IN_DEVELOPMENT && result.token) {
          setLocalValue('memberAuthToken', result.token);
        }

        dispatch(syncMember());
        if (rememberMe) {
          // Save username/email to browser
          dispatch(setSavedUsername(email));
        } else {
          dispatch(removeSavedUsername());
        }

        onSuccess?.();
      },
      () => {
        setReCaptchaToken(null);
        setSubmitting(false);
      },
      mfaToken => setMfaToken(mfaToken),
    );
  };
  const formik = useFormik({
    initialValues: {
      email: savedUsername ?? '',
      password: '',
      mfaCode: '',
      rememberMe: !!savedUsername,
    },
    validationSchema,
    onSubmit,
  });
  useEffect(() => {
    if (reCaptchaToken && enableReCaptcha) formik.submitForm();
  }, [reCaptchaToken, enableReCaptcha]);
  return (
    <div style={styles.innerContainer}>
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit} style={styles.form}>
          <FormTextField
            name="email"
            type="email"
            required
            label={t('field.signIn.email')}
            onChange={e => {
              setMfaToken(null);
              formik.setFieldValue('mfaCode', '');
              formik.handleChange(e);
            }}
            autoFocus
          />

          <FormTextField
            name="password"
            type="password"
            required
            label={t('field.signIn.password')}
            onChange={e => {
              setMfaToken(null);
              formik.setFieldValue('mfaCode', '');
              formik.handleChange(e);
            }}
          />
          {Boolean(mfaToken) && (
            <FormTextField
              id="mfaCode"
              name="mfaCode"
              type="mfaCode"
              label={t('field.signIn.mfaCode')}
              required
              onChange={formik.handleChange}
            />
          )}

          <FormCheckBox
            name="rememberMe"
            onChange={formik.handleChange}
            label={t(`field.signIn.rememberMe`)}
          />

          <div>
            <ScreenLink
              label={t('link.forgotPassword.prefix')}
              actionLabel={t('link.forgotPassword.action')}
              url="#"
              onClick={() => dispatch(setCurrentModal(RESET_PASSWORD_MODAL_ID))}
              themeKeyLabel="forgotPasswordLinkText"
              themeKeyActionLabel="forgotPasswordLinkTextRight"
            />
          </div>

          {enableReCaptcha && !reCaptchaToken && (
            <div style={styles.captchaContainer}>
              <ReCAPTCHA
                sitekey={reCaptchaSiteKey!}
                onChange={token => setReCaptchaToken(token)}
                onErrored={() => {
                  setReCaptchaToken(null);
                  enqueueErrorSnackbar(t('problemWithReCaptchaMessage'));
                }}
                size={deviceTypeMobile ? 'compact' : 'normal'}
              />
            </div>
          )}

          <StandardButton
            label={mfaToken ? t('button.signInWithCode') : t('button.signIn')}
            onClick={() => formik.submitForm()}
            disabled={
              !formik.isValid || formik.isSubmitting || (enableReCaptcha && !reCaptchaToken)
            }
            showSpinner={formik.isSubmitting}
            containerStyle={styles.loginButton}
            themeKey="signInSubmitButton"
          />

          <input type="submit" style={styles.hiddenSubmitButton} />
        </Form>
      </FormikProvider>

      <div style={styles.registerSection}>
        <ScreenLink
          label={t('link.register.prefix')}
          actionLabel={t('link.register.action')}
          url="#"
          onClick={() => dispatch(setCurrentModal(REGISTER_MODAL_ID))}
          themeKeyLabel="registerLinkText"
          themeKeyActionLabel="registerLinkTextRight"
        />
      </div>

      <OAuthlogin />

      {guestOrderingEnabled && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <ScreenLink
            label={t('link.guestSignIn.prefix')}
            actionLabel={t('link.guestSignIn.action')}
            url={'#'}
            onClick={() => {
              dispatch(setCurrentModal(null));
            }}
            themeKeyLabel="guestLinkText"
            themeKeyActionLabel="guestLinkTextRight"
          />
        </div>
      )}
    </div>
  );
};

export default SignInForm;

const styles: Styles = {
  // do this otherwise any children without a minSize will
  // be squashed if there isn't room (instead of overflow scroll)
  innerContainer: {
    minHeight: 'min-content',
  },
  registerSection: {
    lineHeight: 2,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
    marginBottom: 34,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  loginButton: {
    marginTop: 20,
    height: 40,
    borderRadius: 5,
  },

  // required to stop iOS from adding a broken "GO" button to the keyboard
  hiddenSubmitButton: {
    visibility: 'hidden',
    position: 'absolute',
  },

  captchaContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    // maxWidth: 260,
    marginTop: 15,
  },
};
